import Phaser from "phaser";
import danAccessoriesRaw from "../data/dan-accessories.json";

/**
 * Character-animation pipeline (art-pipeline de-risk) - now driving REAL art.
 *
 * The path proven end-to-end: raw per-state sprite sheets → `tools/pack-character.mjs`
 * (downsample + shared-baseline crop + downscale + pack) → an atlas (PNG strip +
 * JSON-hash frame map) → `load.atlas` in PreloadScene → the animations registered
 * here → a velocity/input state machine that swaps idle / run / jump / throw.
 *
 * To repack after art changes: `node tools/pack-character.mjs`. To add Lucas/
 * Andrew: pack their atlases under new keys and generalize the "dan-" prefix.
 * Frame COUNTS here must match what the packer emits (see STATES in that tool).
 */
export const CHAR_ATLAS = "char-dan";

// Shared playback speed (from preview.html tuning). The per-state frame COUNTS
// are read from the loaded atlas at register time, so this works whether the art
// was packed from tuned windows or exported pre-trimmed - no hardcoded counts.
const FPS = 15;

// Standing jump: idle-jump frames 0..JUMP_AIR_END are the airborne portion (the
// last is held until landing); the frames after are the landing recovery.
const JUMP_AIR_END = 9;

// The default "still" pose is a single frame (the first idle frame). The full
// idle sheet is a "fidget" the state machine plays after a spell of standing
// perfectly still; where it rests afterward is per-character (see IdleBehavior).
const IDLE_REST_FRAME = "dan-idle-0";

/** Per-character idle behavior. When a character stands perfectly still for
 *  `afterMs`, the idle animation plays once as a fidget. `hold` picks the frame
 *  they rest on when it finishes ("end" freezes on the last frame, "start"
 *  returns to the first). `repeat` re-fires it every `afterMs` of continued
 *  stillness; when false it plays only once per still spell (until they move). */
export interface IdleBehavior {
  afterMs: number;
  hold: "end" | "start";
  repeat: boolean;
}

/** Dan: idle once, 30s after he goes still, and freeze on its final frame. */
export const DAN_IDLE: IdleBehavior = { afterMs: 30_000, hold: "end", repeat: false };

/**
 * Per-frame placement of the thrown ball, authored in tools/throw.html. Offsets are
 * from the sprite origin (worldX = x + facing*ox, worldY = y + oy, y down, px).
 * `front` draws the ball over Dan, else behind. `release` is the frame the ball
 * leaves the hand; the caller holds it for earlier frames and launches at release.
 */
interface ThrowFrame { ox: number; oy: number; front: boolean }
interface ThrowAlign { release: number; offsets: ThrowFrame[] }
const DAN_THROW: { idle: ThrowAlign; run: ThrowAlign } = {
  idle: {
    release: 7,
    offsets: [
      { ox: -15, oy: 7, front: false },
      { ox: -20, oy: 2, front: false },
      { ox: -22, oy: -9, front: true },
      { ox: -15, oy: -16, front: true },
      { ox: -12, oy: -15, front: true },
      { ox: -4, oy: -19, front: false },
      { ox: 21, oy: -11, front: false },
      { ox: 0, oy: 0, front: true },
      { ox: 0, oy: 0, front: true },
      { ox: 0, oy: 0, front: true },
      { ox: 0, oy: 0, front: true },
    ],
  },
  run: {
    release: 6,
    offsets: [
      { ox: -17, oy: -12, front: true },
      { ox: -15, oy: -10, front: true },
      { ox: -14, oy: -12, front: true },
      { ox: -14, oy: -16, front: false },
      { ox: 4, oy: -24, front: false },
      { ox: 17, oy: -16, front: false },
      { ox: 15, oy: -5, front: true },
      { ox: 15, oy: -5, front: true },
      { ox: 15, oy: -5, front: true },
      { ox: 15, oy: -5, front: true },
      { ox: 15, oy: -5, front: true },
      { ox: 15, oy: -5, front: true },
      { ox: 15, oy: -5, front: true },
    ],
  },
};

/** The thrown ball's placement this frame, or null if Dan is not mid-throw.
 *  While `released` is false the caller holds the ball at (ox, oy) with depth
 *  `front`; once true it should launch the projectile from (launchOx, launchOy),
 *  the last in-hand position (the frame before release). */
export interface ThrowBall {
  ox: number; oy: number; front: boolean; released: boolean; launchOx: number; launchOy: number;
}
export function throwBallState(player: Phaser.Physics.Arcade.Sprite): ThrowBall | null {
  const cur = player.anims.currentAnim?.key;
  if (cur !== A.throwIdle && cur !== A.throwRun) return null;
  const align = cur === A.throwRun ? DAN_THROW.run : DAN_THROW.idle;
  const offs = align.offsets;
  const m = (player.frame?.name ?? "").match(/-(\d+)$/);
  const f = m ? Math.min(+m[1], offs.length - 1) : 0;
  const o = offs[f];
  const li = Math.max(0, Math.min(offs.length - 1, align.release - 1)); // last in-hand frame
  const lo = offs[li];
  return { ox: o.ox, oy: o.oy, front: o.front, released: f >= align.release, launchOx: lo.ox, launchOy: lo.oy };
}

// Worn accessories (upgrades), placed per animation frame. Data is authored in
// tools/accessories.html and stored in dan-accessories.json.
export interface AccessoryXf { ox: number; oy: number; rot: number; sx: number; sy: number; front: boolean }
type AccFrame = { ox: number | null; oy: number | null; rot: number | null; sx: number | null; sy: number | null; front: boolean | null };
const ACCESSORIES = (danAccessoriesRaw as unknown as {
  accessories: Record<string, Record<string, AccFrame[]>>;
}).accessories;

/** Placement for `accessory` on the frame Dan is currently showing, or null when
 *  this animation/frame has no data. The current frame name (dan-<state>-<n>)
 *  selects it, so it works for any animation that maps to a packed state. */
export function accessoryTransform(
  player: Phaser.Physics.Arcade.Sprite,
  accessory: string,
): AccessoryXf | null {
  const acc = ACCESSORIES[accessory];
  if (!acc) return null;
  const m = (player.frame?.name ?? "").match(/^dan-(.+)-(\d+)$/);
  if (!m) return null;
  const arr = acc[m[1]];
  if (!arr || !arr.length) return null;
  const o = arr[Math.min(+m[2], arr.length - 1)];
  if (!o || o.ox == null) return null;
  return { ox: o.ox, oy: o.oy!, rot: o.rot!, sx: o.sx!, sy: o.sy!, front: o.front !== false };
}

const A = {
  idle: "dan-idle",
  run: "dan-run",
  // standing jump plays through as a clip (airborne portion, then a landing
  // recovery); running jump stays a rise/fall pose pair chosen by vertical speed
  jumpUpIdle: "dan-jump-up-idle",
  jumpLandIdle: "dan-jump-land-idle",
  riseRun: "dan-rise-run",
  fallRun: "dan-fall-run",
  // throw plays a full clip; standing vs running is latched when it starts
  throwIdle: "dan-throw-idle",
  throwRun: "dan-throw-run",
};

// Hitbox, kept fixed across states so level tuning never shifts.
// Hitbox in frame px. The atlas is packed at CONTENT_H=192 (3x the old 64), so these
// are 3x their original values (28x40) and the sprite renders at DAN_SCALE/DAN_ART.
const BODY_W = 84;
const BODY_H = 120;

// Per-animation visual drop (px): seats each pose's feet on the ground WITHOUT
// touching physics. seat() moves the origin and the body offset together, so the
// hitbox stays put while only the art shifts down. Anything unlisted uses DEFAULT.
const DEFAULT_NUDGE = 1;
const NUDGE: Record<string, number> = {
  // Override per animation here, e.g. [A.riseRun]: 2 to drop the running-jump pose.
};

/** Drop the art by its nudge (px), compensating the body offset so the hitbox
 *  does not move. Idempotent: only re-seats when the nudge value changes. */
function seat(player: Phaser.Physics.Arcade.Sprite, animKey: string) {
  const n = NUDGE[animKey] ?? DEFAULT_NUDGE;
  if (player.getData("nudge") === n) return;
  player.setData("nudge", n);
  player.setOrigin(0.5, 0.5 - n / player.height);
  const body = player.body as Phaser.Physics.Arcade.Body;
  body.setOffset((player.width - BODY_W) / 2, player.height - BODY_H - n);
}

let throwMsIdle = 500;
let throwMsRun = 500;
/** How long to hold the throw pose (ms), matching the packed clip at FPS. Pass
 *  whether Dan is running to get the run-throw (longer) vs the standing throw.
 *  Valid after registerCharacterAnims() has run. */
export const getThrowMs = (running: boolean) => (running ? throwMsRun : throwMsIdle);

/** Register Dan's animations once on the (game-global) animation manager.
 *  Safe to call from any scene's create() - guarded so it builds them once. */
export function registerCharacterAnims(scene: Phaser.Scene) {
  const anims = scene.anims;
  if (anims.exists(A.run)) return;

  const names = scene.textures.get(CHAR_ATLAS).getFrameNames();
  // Exact numeric suffix so "idle" counts dan-idle-<n> only, NOT dan-idle-jump-<n>.
  const count = (state: string) => names.filter((n) => new RegExp(`^dan-${state}-\\d+$`).test(n)).length;

  // idle is registered as a ONE-SHOT fidget (not a loop): the still state shows a
  // single frame; this whole cycle plays once, on a timer (see updateCharacterAnim).
  const ic = count("idle");
  if (ic > 0)
    anims.create({
      key: A.idle,
      frames: anims.generateFrameNames(CHAR_ATLAS, { prefix: "dan-idle-", start: 0, end: ic - 1 }),
      frameRate: FPS,
      repeat: 0,
    });

  // run: looping cycle
  const rc = count("run");
  if (rc > 0)
    anims.create({
      key: A.run,
      frames: anims.generateFrameNames(CHAR_ATLAS, { prefix: "dan-run-", start: 0, end: rc - 1 }),
      frameRate: FPS,
      repeat: -1,
    });

  // Standing jump plays as a clip: frames 0..JUMP_AIR_END are airborne (held on
  // the last until landing), the rest are the landing recovery, after which Dan
  // returns to idle. See updateCharacterAnim.
  const ijc = count("idle-jump");
  if (ijc > 0) {
    const airEnd = Math.min(JUMP_AIR_END, ijc - 1);
    anims.create({
      key: A.jumpUpIdle,
      frames: anims.generateFrameNames(CHAR_ATLAS, { prefix: "dan-idle-jump-", start: 0, end: airEnd }),
      frameRate: FPS,
      repeat: 0,
    });
    if (ijc > airEnd + 1)
      anims.create({
        key: A.jumpLandIdle,
        frames: anims.generateFrameNames(CHAR_ATLAS, { prefix: "dan-idle-jump-", start: airEnd + 1, end: ijc - 1 }),
        frameRate: FPS,
        repeat: 0,
      });
  }

  // Running jump stays a rise/fall pose pair, picked live from vertical velocity.
  const pose = (key: string, state: string, t: number) => {
    const n = count(state);
    if (n > 0)
      anims.create({ key, frames: [{ key: CHAR_ATLAS, frame: `dan-${state}-${Math.floor(n * t)}` }], frameRate: 1 });
  };
  pose(A.riseRun, "run-jump", 0.3);
  pose(A.fallRun, "run-jump", 0.72);

  // Throw plays the whole clip once; standing and running are separate sequences.
  const oneShot = (key: string, state: string) => {
    const n = count(state);
    if (n > 0)
      anims.create({
        key,
        frames: anims.generateFrameNames(CHAR_ATLAS, { prefix: `dan-${state}-`, start: 0, end: n - 1 }),
        frameRate: FPS,
        repeat: 0,
      });
    return n;
  };
  throwMsIdle = Math.round((Math.max(1, oneShot(A.throwIdle, "idle-throw")) / FPS) * 1000);
  throwMsRun = Math.round((Math.max(1, oneShot(A.throwRun, "run-throw")) / FPS) * 1000);
}

/** Point a freshly-created player sprite at the atlas with a 28×40 hitbox,
 *  bottom-centered in the art frame. The box is proportional to the 48×64 art
 *  (same body-to-frame ratio the old 22×30 had on the 36×48 art); feet stay
 *  planted at the frame bottom, so jump arcs and platform standing are unchanged. */
export function useCharacterSprite(player: Phaser.Physics.Arcade.Sprite, idle: IdleBehavior = DAN_IDLE) {
  player.setTexture(CHAR_ATLAS, "dan-idle-0");
  const body = player.body as Phaser.Physics.Arcade.Body;
  body.setSize(BODY_W, BODY_H);
  player.setFrame(IDLE_REST_FRAME); // rest on the first idle frame; fidget is timer-driven
  seat(player, A.idle); // origin + body offset: feet at frame bottom, minus the idle nudge
  player.setData("idle", idle);
  player.setData("stillSince", undefined);
  player.setData("fidgeting", false);
  player.setData("idleSpent", false);
}

/**
 * Per-frame: choose the animation from physics state and face travel. `throwUntil`
 * is the time.now timestamp the throw pose should hold until; each new throw sets a
 * fresh value, which restarts the throw clip (so rapid, repeated throws re-trigger).
 * Call once per update() after movement is applied.
 */
export function updateCharacterAnim(player: Phaser.Physics.Arcade.Sprite, throwUntil: number) {
  const body = player.body as Phaser.Physics.Arcade.Body;
  const vx = body.velocity.x;
  const moving = Math.abs(vx) > 5;

  // face travel; keep last facing when idle (art faces right)
  if (moving) player.setFlipX(vx < 0);

  const cur = player.anims.currentAnim?.key;
  const now = player.scene.time.now;
  const throwing = now < throwUntil;
  // any non-still action clears the "standing still" clock
  const busy = () => {
    player.setData("stillSince", undefined);
    player.setData("fidgeting", false);
    player.setData("idleSpent", false);
  };

  // throw wins while its window is open. A new throw pushes throwUntil to a fresh
  // value; detecting that restarts the clip (so rapid throws re-trigger) and
  // re-latches the standing vs running variant for that throw.
  if (throwing) {
    busy();
    const isNewThrow = player.getData("throwUntil") !== throwUntil;
    if (isNewThrow) {
      player.setData("throwUntil", throwUntil);
      player.setData("throwRunning", moving);
    }
    const want = player.getData("throwRunning") ? A.throwRun : A.throwIdle;
    seat(player, want);
    if (isNewThrow || cur !== want) player.play(want); // restart from frame 0 on a new throw
    return;
  }
  if ((cur === A.throwIdle || cur === A.throwRun) && player.anims.isPlaying) {
    busy();
    return;
  }

  // airborne. Standing jump plays its airborne clip (0..JUMP_AIR_END), holding the
  // last frame until landing; running jump uses rise/fall poses by vertical speed.
  if (!body.blocked.down) {
    busy();
    if (!player.getData("airborne")) {
      player.setData("airborne", true);
      player.setData("airRunning", moving); // latch the variant at takeoff
      player.setData("landing", false);
    }
    if (player.getData("airRunning")) {
      const want = body.velocity.y < -20 ? A.riseRun : A.fallRun;
      seat(player, want);
      if (cur !== want) player.play(want, true);
    } else {
      seat(player, A.jumpUpIdle);
      if (cur !== A.jumpUpIdle) player.play(A.jumpUpIdle); // play 0..end once, then hold the last frame
    }
    return;
  }

  // just landed a standing jump: play the landing recovery, then fall through to
  // idle. Moving/throwing on landing skips it (straight to run/throw).
  if (player.getData("airborne")) {
    player.setData("airborne", false);
    const standingLand =
      player.getData("airRunning") === false && !moving && player.scene.anims.exists(A.jumpLandIdle);
    player.setData("landing", standingLand);
    if (standingLand) {
      seat(player, A.jumpLandIdle);
      player.play(A.jumpLandIdle);
      return;
    }
  }
  // hold through the landing recovery until it finishes (moving cancels into a run)
  if (player.getData("landing")) {
    if (!moving && cur === A.jumpLandIdle && player.anims.isPlaying) {
      seat(player, A.jumpLandIdle);
      return;
    }
    player.setData("landing", false);
  }

  // grounded + moving: always run
  if (moving) {
    busy();
    seat(player, A.run);
    // replay when the state changed OR when it was left stopped (e.g. after
    // standing still), so resuming never freezes into a slide
    if (cur !== A.run || !player.anims.isPlaying) player.play(A.run, true);
    return;
  }

  // grounded + still: hold the rest frame; after `idle.afterMs` of stillness the
  // idle plays once as a fidget. Where it settles and whether it re-fires are
  // per-character (see IdleBehavior / useCharacterSprite).
  const idle = (player.getData("idle") as IdleBehavior) ?? DAN_IDLE;
  seat(player, A.idle);

  if (player.getData("fidgeting")) {
    if (player.anims.isPlaying && cur === A.idle) return; // let the fidget play out
    player.setData("fidgeting", false);
    if (idle.hold === "start") {
      player.anims.stop();
      player.setFrame(IDLE_REST_FRAME);
    } // "end": leave Dan on idle's final frame (Phaser holds it after a one-shot)
    if (idle.repeat) player.setData("stillSince", now); // arm the next fidget
    else player.setData("idleSpent", true); // one fidget per still spell
    return;
  }

  if (player.getData("idleSpent")) return; // already fidgeted this spell; hold the frame

  if (player.getData("stillSince") === undefined) {
    player.setData("stillSince", now); // just became still
    player.anims.stop();
    player.setFrame(IDLE_REST_FRAME);
    return;
  }
  if (now - (player.getData("stillSince") as number) >= idle.afterMs) {
    player.setData("fidgeting", true);
    player.play(A.idle); // one-shot fidget
  }
}
